import { useState, type FormEvent } from 'react'
import { useNavigate, useLocation, Navigate } from 'react-router-dom'
import {
  Alert,
  Button,
  Center,
  Container,
  Paper,
  PasswordInput,
  Stack,
  Text,
  TextInput,
  Title,
} from '@mantine/core'
import { IconAlertCircle, IconSnowflake } from '@tabler/icons-react'
import { login } from '../shared/api/auth'
import { useAuthStore, refreshMe } from '../features/auth/store'
import { isApiError } from '../shared/api/client'

export function LoginPage() {
  const { user } = useAuthStore()
  const navigate = useNavigate()
  const location = useLocation()
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [error, setError] = useState<string | null>(null)
  const [submitting, setSubmitting] = useState(false)

  if (user) {
    const from = (location.state as { from?: { pathname: string } })?.from?.pathname ?? '/projects'
    return <Navigate to={from} replace />
  }

  async function onSubmit(e: FormEvent) {
    e.preventDefault()
    setError(null)
    setSubmitting(true)
    try {
      await login(email, password)
      // Блок Б слой 6: грузим ПОЛНЫЙ /me (MeResponse с permissions) в стор, а не
      // UserDto из login-ответа — иначе пункт «Роли и доступы» не появится до рефреша.
      await refreshMe()
      const from = (location.state as { from?: { pathname: string } })?.from?.pathname ?? '/projects'
      navigate(from, { replace: true })
    } catch (err) {
      if (isApiError(err)) {
        const code = err.response?.data?.error?.code
        if (code === 'unauthorized') {
          setError('Неверный email или пароль')
        } else if (code === 'invalid_input') {
          setError('Проверьте формат email и длину пароля')
        } else {
          setError('Сервер недоступен, попробуйте позже')
        }
      } else {
        setError('Сетевая ошибка')
      }
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <Center mih="100vh">
      <Container size={420} w="100%">
        <Stack align="center" gap="xs" mb="xl">
          <IconSnowflake size={48} color="var(--mantine-color-blue-8)" stroke={1.5} />
          <Title order={2} c="blue.9" fw={800}>
            Ledokol CRM
          </Title>
          <Text size="sm" c="dimmed">
            Войдите в систему управления задачами
          </Text>
        </Stack>

        <Paper p="xl" shadow="sm" radius="md" withBorder>
          <form onSubmit={onSubmit}>
            <Stack gap="md">
              <TextInput
                label="Электронная почта"
                placeholder="you@ledokol.uz"
                required
                autoComplete="email"
                value={email}
                onChange={(e) => setEmail(e.currentTarget.value)}
              />
              <PasswordInput
                label="Пароль"
                placeholder="••••••••"
                required
                autoComplete="current-password"
                value={password}
                onChange={(e) => setPassword(e.currentTarget.value)}
              />

              {error && (
                <Alert color="red" icon={<IconAlertCircle size={18} />} variant="light">
                  {error}
                </Alert>
              )}

              <Button type="submit" fullWidth size="md" loading={submitting}>
                Войти
              </Button>
            </Stack>
          </form>
        </Paper>

        <Text size="xs" c="dimmed" ta="center" mt="md">
          Если нет учётной записи — обратитесь к администратору
        </Text>
      </Container>
    </Center>
  )
}
